import React from 'react'
import styled from 'styled-components/macro'
import PropTypes from 'prop-types'
import cleanDeep from 'clean-deep';
import { usePaginatedQuery, useMutation, queryCache } from 'react-query'
import { Spacer, Button } from 'components/atoms'
import { InputIcon, Pagination } from 'components/molecules'
import { PeoplePermissionsTable } from 'components/organisms'
import { PeopleSelectModal, Toast } from 'containers'
import {
  getSubwebUsers,
  SUBWEB_USERS_KEY,
  SUBWEB_USERS_ALL_KEY,
  postSubwebUser,
  deleteSubwebUser
} from 'services/requests'
import { useDebounce } from 'react-use'
import { useToggle } from 'hooks'
import { toast } from 'react-toastify';

const PermissionsList = ({
  subweb
}) => {
  const [search, setSearch] = React.useState('')
  const [debouncedSearch, setDebouncedSearch] = React.useState('');
  const [page, setPage] = React.useState(1)
  const [isOpen, toggle] = useToggle(false)

  const [addSubwebUser, { isLoading: isLoadingAddUser }] = useMutation(postSubwebUser, {
    onSuccess: () => {
      queryCache.invalidateQueries(SUBWEB_USERS_KEY)
      queryCache.invalidateQueries(SUBWEB_USERS_ALL_KEY)
      toast.success(
        <Toast
          type="success"
          title="Utilizador adicionado à subweb"
          description="O utilizador foi adicionado com sucesso."
        />
      );
    },
    onError: (err) => {
      toast.error(
        <Toast
          type="error"
          title="Erro a adicionar utilizador à subweb"
          description={err.message}
        />
      );
    }
  })

  const [removeSubwebUser, { isLoading: isLoadingDeleteUser }] = useMutation(deleteSubwebUser, {
    onSuccess: () => {
      queryCache.invalidateQueries(SUBWEB_USERS_KEY)
      queryCache.invalidateQueries(SUBWEB_USERS_ALL_KEY)
      toast.success(
        <Toast
          type="success"
          title="Utilizador removido da subweb"
          description="O utilizador foi removido com sucesso."
        />
      );
    },
    onError: (err) => {
      toast.error(
        <Toast
          type="error"
          title="Erro a remover utilizador da subweb"
          description={err.message}
        />
      );
    }
  })

  useDebounce(
    () => {
      if (debouncedSearch !== search) setDebouncedSearch(search);
    },
    350,
    [search]
  );

  const {
    isLoading,
    isFetching,
    resolvedData,
  } = usePaginatedQuery([SUBWEB_USERS_KEY, {
    subweb,
    currentPage: page,
    search: debouncedSearch,
  }], getSubwebUsers)

  const totalRecords = React.useMemo(
    () => (
      resolvedData && resolvedData.data
        && resolvedData.data.pagination && resolvedData.data.pagination.totalRecords
        ? resolvedData.data.pagination.totalRecords
        : 0
    ),
    [resolvedData])

  const dataComputed = React.useMemo(() => {
    if (
      resolvedData && resolvedData.data && resolvedData.data.data
        && resolvedData.data.data.length > 0
    ) {
      return resolvedData.data.data.map(({
        id,
        email,
        name,
        pagePermission,
        subwebPermission,
      }) => {
        let permissions = null
        if (pagePermission) permissions = 'páginas'
        if (subwebPermission) permissions = 'subweb'

        const person = {
          id,
          name,
          email: email.length >= 1 ? email[0] : null,
          permissions,
        }
        return cleanDeep(person)
      })
    }
    return []
  }, [resolvedData])

  const handleSearch = (e) => {
    const searchValue = e.target.value
    setSearch(searchValue)
    setPage(1)
  }

  // react-table pages start at 0 while rc-pagination starts at 1
  const tablePage = React.useMemo(() => page - 1, [page])

  const handleAddUser = ({ id }) => {
    addSubwebUser({ subweb, userId: id })
  }

  const handleRemoveUser = ({ id }) => {
    removeSubwebUser({ subweb, userId: id })
  }

  return (
    <>
      <PeopleSelectModal subweb={subweb} toggle={toggle} isOpen={isOpen} onSelect={handleAddUser} />

      <Home.Header>
        <GlobalActions>
          <Button
            modifiers={['dark', 'smallText']}
            onClick={toggle}
            loading={isLoadingAddUser || isLoadingDeleteUser}
          >
            adicionar à subweb
          </Button>
        </GlobalActions>

      </Home.Header>

      <Top>
        <Top.Start>
          <InputIcon
            isLoading={isLoading || isFetching || isLoadingAddUser || isLoadingDeleteUser}
            icon="search"
            placeholder="pesquisar pessoas"
            value={search}
            onChange={handleSearch}
            modifiers={['stretch']}
          />
        </Top.Start>

      </Top>
      <List>
        <Spacer bottom="m">
          <Pagination
            onChange={(nextPage) => setPage(nextPage)}
            current={page}
            total={totalRecords}
          />
        </Spacer>
        <PeoplePermissionsTable
          data={dataComputed}
          page={tablePage}
          loading={isLoading}
          onRemove={handleRemoveUser}
        />
        <Spacer top="m">
          <Pagination
            onChange={(nextPage) => setPage(nextPage)}
            current={page}
            total={totalRecords}
          />
        </Spacer>
      </List>
    </>
  )
}

const Top = styled.div`
  width: 100%;
  padding-bottom: ${props => props.theme.spacing.l};
  display: grid;
  grid-template-columns: 1fr auto;
  grid-column-gap: ${props => props.theme.spacing.xs};
`;

Top.Start = styled.div``;
Top.End = styled.div`
  button {
    height: 100%;
  }
`;
const GlobalActions = styled.div`
position: absolute;
right: 41px;
top: 44px;
`
const Home = styled.div`

${props => props.theme.mixins.select()};
`;

Home.Header = styled.div`
margin-bottom: ${props => props.theme.spacing.l};
`;
const List = styled.div``

PermissionsList.propTypes = {
  subweb: PropTypes.string,
}

PermissionsList.defaultProps = {
  subweb: 'ua',
}

export default PermissionsList
